import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Heart, AlertTriangle, Loader2 } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'
import { productImageUrl } from '../lib/image-url'

interface WishlistRow {
  productoId: string
  sku: string
  nombre: string
  stock: number
  precio: number
  precioOferta: number | null
  imagen: string
  marca: string
  categoria: string
  guardados: number
  sinStock: boolean
}

interface WishlistResponse {
  data: WishlistRow[]
  total: number
}

export function WishlistAnalytics() {
  const [onlyOutOfStock, setOnlyOutOfStock] = useState(false)

  const { data, isLoading, error } = useQuery<WishlistResponse>({
    queryKey: ['analytics-wishlist', onlyOutOfStock],
    queryFn: () =>
      apiGet(`/api/admin/analytics/wishlist?limit=100${onlyOutOfStock ? '&onlyOutOfStock=1' : ''}`),
  })

  const rows = data?.data ?? []
  const outOfStockCount = rows.filter((r) => r.sinStock).length

  return (
    <>
      <Topbar
        title="Wishlist — Analytics"
        subtitle={
          data
            ? `${data.total} productos guardados por clientes · ${outOfStockCount} sin stock`
            : 'Productos más guardados por clientes'
        }
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-ink-700">
            <Heart className="size-4 text-pink-600" />
            Productos más deseados por los clientes — útil para priorizar restock y promos.
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink-800">
            <input
              type="checkbox"
              checked={onlyOutOfStock}
              onChange={(e) => setOnlyOutOfStock(e.target.checked)}
              className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
            />
            Solo sin stock (oportunidades de restock)
          </label>
        </div>

        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            No se pudo cargar el reporte de wishlist.
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Marca / Categoría</th>
                <th className="px-4 py-3 text-right font-medium">Guardados</th>
                <th className="px-4 py-3 text-right font-medium">Stock</th>
                <th className="px-4 py-3 text-right font-medium">Precio</th>
                <th className="px-4 py-3 font-medium">Oportunidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10">
                    <div className="grid place-items-center text-ink-500">
                      <Loader2 className="size-6 animate-spin" />
                    </div>
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-ink-500">
                    {onlyOutOfStock
                      ? 'No hay productos en wishlist sin stock.'
                      : 'Aún no hay datos de wishlist.'}
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.productoId} className="hover:bg-ink-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="grid size-12 place-items-center overflow-hidden rounded bg-ink-100">
                        {r.imagen ? (
                          <img
                            src={productImageUrl(r.imagen)}
                            alt={r.nombre}
                            className="size-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-[10px] text-ink-500">—</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-ink-900">{r.nombre}</div>
                        <div className="font-mono text-xs text-ink-500">{r.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-600">
                    <div>{r.marca || '—'}</div>
                    <div className="text-ink-500">{r.categoria || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 font-semibold text-pink-700">
                      <Heart className="size-3.5 fill-pink-600 text-pink-600" />
                      {r.guardados.toLocaleString('es-MX')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.stock <= 0 ? (
                      <Badge variant="danger">Agotado</Badge>
                    ) : r.stock < 5 ? (
                      <Badge variant="warning">{r.stock}</Badge>
                    ) : (
                      <span className="font-medium text-ink-700">{r.stock}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-ink-800">
                    {r.precioOferta != null ? (
                      <div>
                        <div className="font-semibold text-brand-700">{formatMXN(r.precioOferta)}</div>
                        <div className="text-xs text-ink-400 line-through">{formatMXN(r.precio)}</div>
                      </div>
                    ) : (
                      <span>{formatMXN(r.precio)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.sinStock && r.guardados >= 1 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                        <AlertTriangle className="size-3" />
                        Restock urgente
                      </span>
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && !onlyOutOfStock && outOfStockCount > 0 && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="size-4 shrink-0" />
            Hay <strong>{outOfStockCount}</strong> productos en wishlist sin stock — activa el filtro
            "Solo sin stock" para ver candidatos a restock prioritario.
          </div>
        )}
      </div>
    </>
  )
}

export default WishlistAnalytics
